/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { HFlow, Theme, useTheme } from 'bold-ui'
import { isBefore } from 'date-fns'
import { SituacaoAgendadoEnum } from 'graphql/types.generated'
import { Fragment, useState } from 'react'
import { useAgendaAuthorization } from 'view/agenda/authorization/useAgendaAuthorization'
import { CancelarAgendamentoButton } from 'view/agenda/components/acoes-agendamento/buttons/CancelarAgendamentoButton'
import { ImprimirAgendamentoButton } from 'view/agenda/components/acoes-agendamento/buttons/ImprimirAgendamentoButton'
import { getStatusAcoesConsulta } from 'view/agenda/components/acoes-agendamento/getStatusAcoesConsulta'
import { getStatusAcoesConsultaAD } from 'view/agenda/components/acoes-agendamento/getStatusAcoesConsultaAD'
import JustificativaCancelarAgendamentoModal from 'view/agenda/components/acoes-agendamento/JustificativaCancelarAgendamentoModal'
import MenuAcoesAgendamento from 'view/agenda/components/acoes-agendamento/MenuAcoesAgendamento'
import { AcaoAgendamentoEnum } from 'view/agenda/components/acoes-agendamento/model-acoesAgendamento'
import { excluirAgendamentoTooltip } from 'view/agenda/components/acoes-agendamento/utils'
import { AgendamentoFooterProps } from 'view/agenda/components/AgendamentoFooter'

import { AgendamentoCidadaoModel, AgendamentoCidadaoPermissions, LotacaoAgendadaModel } from '../model-cidadao'
import { AgendamentoCidadaoSection } from './AgendamentoCidadaoSection'
import { convertAgendamentoCidadao } from './converter-agendamentoCidadao'
import { AgendamentoCidadaoStatus } from './model-agendamentoCidadao'

interface AgendamentosCidadaoListingItemProps extends Pick<AgendamentoFooterProps, 'isAgendaOnlineAtiva'> {
  agendamento: AgendamentoCidadaoModel
  permissions: AgendamentoCidadaoPermissions
  profissionalId: ID
  equipeId: ID
  ubsId: ID
  serverTime: Date
  onCancelar?: () => void
}

export function AgendamentosCidadaoListingItem(props: AgendamentosCidadaoListingItemProps) {
  const {
    agendamento,
    permissions: { podeCancelarTodasEquipes, podeCancelarAgendamento },
    profissionalId,
    equipeId,
    ubsId,
    serverTime,
    isAgendaOnlineAtiva,
    onCancelar,
  } = props

  const { isAtencaoDomiciliar, lotacaoAgendada } = agendamento

  const theme = useTheme()

  const [isModalJustificativaOpen, setModalJustificativaOpen] = useState(false)
  const { hasAuthorizationCancelar } = useAgendaAuthorization(isAtencaoDomiciliar)
  const styles = createStyles(theme)

  const isPassado = isBefore(agendamento.horarioInicial, serverTime)
  const { isMesmaUbs, isOutroProfissionalDeOutraEquipe } = getStatusAgendamentoCidadao(
    lotacaoAgendada,
    profissionalId,
    equipeId,
    ubsId
  )
  const showCancelar =
    agendamento.situacao === SituacaoAgendadoEnum.AGENDADO &&
    podeCancelarAgendamento &&
    isMesmaUbs &&
    hasAuthorizationCancelar(lotacaoAgendada)

  const isCancelarDisabled =
    isPassado || isAtencaoDomiciliar || (isOutroProfissionalDeOutraEquipe && !podeCancelarTodasEquipes)

  const agendaEvent = convertAgendamentoCidadao(agendamento)

  const statusAcoes = isAtencaoDomiciliar
    ? getStatusAcoesConsultaAD({ dataConsulta: agendaEvent.start, dataAtual: serverTime })
    : getStatusAcoesConsulta({
        situacao: agendaEvent.situacao,
        situacaoAtendimento: agendaEvent.atendimento?.situacao,
        possuiAtendimentoPrevio: agendaEvent.atendimento?.possuiAtendimentosProfissionais,
        isForaUbs: agendaEvent.isForaUbs,
        dataAtual: serverTime,
        dataConsulta: new Date(agendaEvent.start),
      })

  const statusImprimirAgendamento = statusAcoes[AcaoAgendamentoEnum.IMPRIMIR_AGENDAMENTO]

  return (
    <Fragment>
      <HFlow hSpacing={0} style={styles.wrapper} justifyContent='space-between' alignItems='flex-start'>
        <AgendamentoCidadaoSection agendamento={agendamento} isAgendaOnlineAtiva={isAgendaOnlineAtiva} />
        <MenuAcoesAgendamento>
          <ImprimirAgendamentoButton
            event={agendaEvent}
            disabled={statusImprimirAgendamento.disabled}
            tooltip={statusImprimirAgendamento.tooltip}
            isDropdown
          />
          {showCancelar && (
            <CancelarAgendamentoButton
              onAction={onCancelar}
              event={agendaEvent}
              openModalCancelarAgendamento={() => setModalJustificativaOpen(true)}
              disabled={isCancelarDisabled}
              tooltip={excluirAgendamentoTooltip(isPassado, isOutroProfissionalDeOutraEquipe, isAtencaoDomiciliar)}
              isDropdown
            />
          )}
        </MenuAcoesAgendamento>
      </HFlow>
      <JustificativaCancelarAgendamentoModal
        onAction={onCancelar}
        event={agendaEvent}
        open={isModalJustificativaOpen}
        setOpen={setModalJustificativaOpen}
      />
    </Fragment>
  )
}

const createStyles = (theme: Theme) => ({
  wrapper: css`
    background-color: ${theme.pallete.surface.main};
    border: 1px ${theme.pallete.divider} solid;
    border-radius: 2px;
    padding-left: 1.5rem;
    padding-right: 0.5rem;
    padding-top: 0.5rem;
  `,
  item: css`
    padding: 0.5rem 1rem;
  `,
})

function getStatusAgendamentoCidadao(
  lotacaoAgendada: LotacaoAgendadaModel,
  profissionalId: ID,
  equipeId: ID,
  ubsId: ID
): AgendamentoCidadaoStatus {
  const isMesmaUbs = lotacaoAgendada.unidadeSaude.id === ubsId
  const isOutroProfissionalDeOutraEquipe =
    lotacaoAgendada.profissional.id !== profissionalId &&
    lotacaoAgendada.equipe !== null &&
    lotacaoAgendada.equipe.id !== equipeId
  return { isMesmaUbs, isOutroProfissionalDeOutraEquipe }
}
